/**
 * account-mobile-settings.tsx — Màn "Cài đặt" trên ĐIỆN THOẠI (/account), bám mẫu Cài đặt của Lark Mobile: nền xám nhạt, các thẻ nhóm
 * bo tròn rộng gần hết bề ngang, mỗi dòng cao ~56px (chữ 16px) + mũi tên, thẻ "Đăng xuất" riêng ở cuối. Lối vào: ngăn Cá nhân
 * (mobile-account-drawer.tsx) → Cài đặt. Dữ liệu các nhóm ở account-mobile-lib.ts.
 */
import { useMemo } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { ChevronLeft, ChevronRight, Monitor } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { navForRole } from '@/components/shared/nav-config'
import { useMyPermissions } from '@/hooks/use-settings'
import { buildMobileApps } from '@/lib/mobile-apps'
import { useAuthStore } from '@/stores/auth-store'
import { useUiStore, type ThemePref } from '@/stores/ui-store'
import { buildSettingsGroups } from './account-mobile-lib'

export function AccountMobileSettings() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation(['accountPages', 'shell', 'common'])
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)
  const themePref = useUiStore((s) => s.themePref)
  const { data: perms } = useMyPermissions()
  const orgSettings = useMemo(() => buildMobileApps(navForRole(user?.role), perms).some((a) => a.to === '/settings'), [user?.role, perms])
  const themeLabel = (p: ThemePref) => t(`shell:userMenu.themes.${p}`)
  const groups = buildSettingsGroups({ themeLabel: themeLabel(themePref) ?? themeLabel('system'), orgSettings })
  const version = import.meta.env.VITE_APP_VERSION as string | undefined

  // Có trang trước trong phiên (vd Nhắn tin) thì quay lại đó; mở thẳng đường dẫn này thì về Nhắn tin.
  const back = () => (location.key !== 'default' ? navigate(-1) : navigate('/conversations', { replace: true }))

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden bg-muted/50 dark:bg-black/25">
      <header className="relative flex h-12 items-center px-2 pt-[env(safe-area-inset-top)]">
        <button type="button" onClick={back} aria-label={t('common:actions.back')} className="flex h-10 w-10 items-center justify-center rounded-full active:bg-accent">
          <ChevronLeft className="h-6 w-6" />
        </button>
        <h1 className="pointer-events-none absolute inset-x-12 text-center text-base font-semibold">{t('accountPages:mobile.title')}</h1>
      </header>

      <div className="space-y-3 px-3 pb-8 pt-2">
        {groups.map((g) => (
          <nav key={g.key} aria-label={g.key} className="divide-y overflow-hidden rounded-2xl border bg-card">
            {g.rows.map((r) => (
              <Link key={r.key} to={r.to} className="flex min-h-14 items-center gap-3 px-4 text-[16px] active:bg-accent">
                <span className="min-w-0 flex-1 truncate">{r.label}</span>
                {r.pc && (
                  <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold leading-none text-muted-foreground">
                    <Monitor className="h-2.5 w-2.5" /> PC
                  </span>
                )}
                {r.value && <span className="max-w-[40%] shrink-0 truncate text-sm text-muted-foreground">{r.value}</span>}
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              </Link>
            ))}
          </nav>
        ))}

        <button
          type="button"
          onClick={async () => {
            await logout()
            navigate('/login')
          }}
          className="flex min-h-14 w-full items-center justify-center rounded-2xl border bg-card text-[16px] font-medium text-destructive active:bg-accent"
        >
          {t('common:actions.logout')}
        </button>

        {version && <p className="pt-1 text-center text-xs text-muted-foreground">{t('accountPages:mobile.version', { version })}</p>}
      </div>
    </div>
  )
}
